import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAnnotations,
  listTags,
  setTags,
  addNote,
  deleteNote,
  type AdAnnotation,
} from '../api/adAnnotations';

const ANNOTATIONS_QUERY = ['ad-annotations'] as const;
const TAGS_QUERY = ['ad-annotation-tags'] as const;

/**
 * Fetch every annotation visible to the user (no account scoping —
 * dashboards toggle between "all accounts" and a single account, so we
 * keep the index local).
 */
export function useAdAnnotations() {
  return useQuery({
    queryKey: ANNOTATIONS_QUERY,
    queryFn: () => listAnnotations(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useDistinctTags() {
  return useQuery({
    queryKey: TAGS_QUERY,
    queryFn: () => listTags(),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Lookup: `${accountId}:${adId}` -> AdAnnotation
 */
export function useAnnotationIndex(): Map<string, AdAnnotation> {
  const { data } = useAdAnnotations();
  return useMemo(() => {
    const map = new Map<string, AdAnnotation>();
    for (const a of data ?? []) {
      map.set(`${a.accountId}:${a.adId}`, a);
    }
    return map;
  }, [data]);
}

export function annotationKey(accountId: string, adId: string): string {
  return `${accountId}:${adId}`;
}

export function useSetTagsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { accountId: string; adId: string; tags: string[] }) =>
      setTags(vars.accountId, vars.adId, vars.tags),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ANNOTATIONS_QUERY });
      qc.invalidateQueries({ queryKey: TAGS_QUERY });
    },
  });
}

export function useAddNoteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { accountId: string; adId: string; text: string }) =>
      addNote(vars.accountId, vars.adId, vars.text),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ANNOTATIONS_QUERY });
    },
  });
}

export function useDeleteNoteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { annotationId: string; noteId: string }) =>
      deleteNote(vars.annotationId, vars.noteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ANNOTATIONS_QUERY });
    },
  });
}
